import * as React from 'react';
import io from 'socket.io-client';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { UserContext } from '../context/UserContext';
import { getAccount } from '../controllers/accounts/accounts';
import { createComment } from '../controllers/posts/comments';
import { createConversation } from '../controllers/conversation/conversation';
import { getCheckins } from '../controllers/checkin/checkin';
import { InPersonBadge } from '../components/InPersonBadge';

// Mounted once at the app root (alongside LocationTracker) so the socket
// connection — and the alert it can show — survives route changes instead of
// being torn down/recreated on every page navigation.
export const CheckinAlertListener = () => {
  const { userDetails } = React.useContext(UserContext);
  const token = userDetails?.token;
  const myId = userDetails?.id;
  const queryClient = useQueryClient();
  const [alert, setAlert] = React.useState(null); // { event_id, account }
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!token || !myId) return;

    // socket.io-client treats any path in the connection URL as a namespace to
    // join, not a route, so pass just the origin (see the speed-meeting fix).
    const base = process.env.REACT_APP_MKGF_MASTODON_API;
    const socket = io(new URL(base).origin, { auth: { token } });

    socket.on('connect_error', (err) => {
      console.error('CheckinAlertListener: socket connect_error', err.message);
    });

    socket.on('user-checked-in', async ({ event_id, checkin_user_id }) => {
      try {
        const [account, checkins] = await Promise.all([
          getAccount(checkin_user_id, token),
          getCheckins(token, event_id),
        ]);
        const row = checkins?.checked_in?.find(c => c.user === checkin_user_id);
        setAlert({ event_id, account, isInPerson: !!row?.is_in_person });
      } catch (err) {
        console.error('CheckinAlertListener: failed to resolve check-in account', err);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [token, myId]);

  const handleIgnore = () => { setAlert(null); setError(null); };

  const handleParty = async () => {
    if (!alert?.account?.id || sending) return;
    setSending(true);
    setError(null);
    try {
      const status = await createComment({
        body: {
          status: '@' + alert.account.acct + " let's party! 🎉",
          // Mingle pairings form before either side necessarily follows the
          // other, and Mastodon direct messages only deliver between accounts
          // that follow each other — public keeps the mention-based reply
          // thread working regardless of follow state.
          visibility: 'public',
        },
        token,
      });
      // fetch() doesn't throw on 4xx/5xx responses, and Mastodon returns a
      // plain {error: "..."} body on rejection (e.g. a malformed/empty
      // status) — status.id being missing is the only signal something went
      // wrong, so it has to be checked explicitly rather than assumed.
      if (!status?.id) {
        throw new Error('Mastodon rejected the status: ' + JSON.stringify(status));
      }
      const conversation = await createConversation({
        token,
        participants: [myId, alert.account.id],
        status_id: status.id,
        event_id: alert.event_id,
        author_id: myId,
      });
      if (!conversation?.conversation?._id) {
        throw new Error('Server rejected the conversation: ' + JSON.stringify(conversation));
      }
      // Mingle reads from this query to build its avatar list — without
      // invalidating it here the new conversation only shows up once
      // something else happens to refetch it (e.g. reloading the page).
      queryClient.invalidateQueries({ queryKey: ['getMyEventConversations'] });
      setAlert(null);
    } catch (err) {
      console.error('CheckinAlertListener: Let\'s Party failed', err);
      setError(err.message || 'Something went wrong starting this conversation.');
    } finally {
      setSending(false);
    }
  };

  if (!alert) return null;

  return (
    <Dialog open onClose={handleIgnore}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar src={alert.account?.avatar} />
        <Typography component="span" sx={{ flex: 1 }}>
          {alert.account?.display_name || alert.account?.username} has checked in.
        </Typography>
        <InPersonBadge isInPerson={alert.isInPerson} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Want to party with them at this event?
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleIgnore} disabled={sending}>Ignore</Button>
        <Button variant="contained" onClick={handleParty} disabled={sending}>
          {sending ? 'Starting…' : "Let's Party"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
